import React, {useMemo} from 'react';

import {SocialSharingMeta, MetaProps} from '@yandex-data-ui/common';

import {compose} from 'recompose';
import withRouter, {WithRouterProps} from 'hoc/withRouter';
import withLocale, {WithLocaleProps} from 'hoc/withLocale';

import {getLocaleData, getSharingImageURL, sanitizeHtml} from 'utils';
import DocumentMeta, {DocumentMetaProps} from 'components/DocumentMeta/DocumentMeta';
import {getAbsoluteLocaleUrl, getLocaleByLang} from '../../../common/i18n/utils';

import metaConfig from 'settings/sharing.yaml';

export interface SharingConfig {
    title?: string;
    description?: string;
    customImage?: string;
}

interface SharingProps {
    title?: string;
    description?: string;
}

export interface MetaComponentProps extends DocumentMetaProps {
    type?: string;
    url?: string;
    image?: string;
    locale?: string;
    sharing?: SharingProps;
    extra?: MetaProps[];
    description?: string;
    keywords?: string | string[];
    noIndex?: boolean;
    schemaJsonLd?: unknown;
    canonical?: string;
}

type MetaComponentInnerProps = MetaComponentProps & WithRouterProps & WithLocaleProps;

const Meta: React.FC<MetaComponentInnerProps> = (props) => {
    const {
        type = 'website',
        url,
        sharing,
        image,
        extra = [],
        schemaJsonLd,
        locale,
        router,
        description,
        keywords,
        noIndex,
        canonical,
        ...documentMetaProps
    } = props;

    const commonSharing = useMemo(
        () => getLocaleData<SharingConfig>(metaConfig, locale?.code),
        [locale?.code],
    );

    const sharingTitle = useMemo(
        () => sanitizeHtml(sharing?.title) ?? commonSharing?.title,
        [commonSharing?.title, sharing?.title],
    );

    const sharingDescription = useMemo(
        () => sanitizeHtml(sharing?.description) ?? commonSharing?.description,
        [commonSharing?.description, sharing?.description],
    );

    const metaDescription = useMemo(() => sanitizeHtml(description), [description]);

    const metaKeywords = useMemo(
        () => (Array.isArray(keywords) ? keywords.join(',') : keywords),
        [keywords],
    );

    const metaSchemaJsonLd = useMemo(
        () => (schemaJsonLd ? JSON.stringify(schemaJsonLd) : undefined),
        [schemaJsonLd],
    );

    const baseUrl = useMemo(
        () => new URL(router.as, `https://${router.hostname}`).href,
        [router.as, router.hostname],
    );

    const metaImage = useMemo(
        () => image || getSharingImageURL(locale, router),
        [image, locale, router],
    );

    const defaultLangLocale = useMemo(() => getLocaleByLang(locale.lang), [locale.lang]);

    const canonicalUrl = useMemo(
        () =>
            locale.tld === defaultLangLocale.tld
                ? getAbsoluteLocaleUrl(defaultLangLocale, router.hostname, canonical ?? baseUrl)
                : null,
        [baseUrl, canonical, defaultLangLocale, locale.tld, router.hostname],
    );

    const extraMeta = useMemo(() => {
        const extraProperties = Array.from(extra);

        if (!extraProperties.find((metaTag) => metaTag.property === 'share:title')) {
            extraProperties.push({property: 'share:title', content: sharingTitle});
        }

        if (!extraProperties.find((metaTag) => metaTag.property === 'share:sharing_schema')) {
            extraProperties.push({property: 'share:sharing_schema', content: 'default'});
        }
        return extraProperties;
    }, [extra, sharingTitle]);

    return (
        <React.Fragment>
            <DocumentMeta {...documentMetaProps} />
            <SocialSharingMeta
                type={type}
                url={url || router.pathname}
                locale={locale?.code}
                title={sharingTitle}
                description={sharingDescription}
                image={metaImage}
                extra={extraMeta}
            >
                <base href={baseUrl} />
                {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                {noIndex && <meta name="robots" content="noindex" />}
                {description && <meta name="description" content={metaDescription} />}
                {keywords && <meta name="keywords" content={metaKeywords} />}
                {metaSchemaJsonLd && (
                    <script id="schema" type="application/ld+json">
                        {metaSchemaJsonLd}
                    </script>
                )}
            </SocialSharingMeta>
        </React.Fragment>
    );
};

export default compose<MetaComponentInnerProps, MetaComponentProps>(withRouter, withLocale)(Meta);
